import { Injectable } from '@angular/core';

import { BetState } from '@bpos/v1/my-bets';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';

import { MyBetsBet, MyBetsBetSlip } from '../../my-bets-base/models/my-bets-viewmodels';
import { BetMarketInfo, VisualizationBetState, VisualizationConstants, VisualizationType } from '../models/my-bets-visualization-types';
import { MyBetsVisualizationService } from './my-bets-visualization.service';

@Injectable()
export class MyBetsVisualizationTrackingService {
    constructor(
        private sitecore: Sitecore,
        private myBetsVisualizationService: MyBetsVisualizationService,
    ) {}

    getBetTypeInfo(betslip: MyBetsBetSlip): string {
        return betslip.bets
            .map((bet) => (bet.betBuilderInfo ? this.constructBetBuilderInfo(bet, betslip) : this.constructBetTypeInfo(bet, betslip)))
            .join(';');
    }

    private constructBetTypeInfo(bet: MyBetsBet, betslip: MyBetsBetSlip): string {
        const betMarketInfo = this.myBetsVisualizationService.getBetMarketInfoFromBet(bet);
        const visualizationType = this.myBetsVisualizationService.getVisualizationDetails(bet.event, bet.sportId, bet.marketId).visualizationType;
        const betState = this.getBetState(bet, betslip.isCachedOut, visualizationType, betMarketInfo);

        return this.buildBetInfoString(betslip.sgpType ?? betslip.betType, visualizationType, bet.optionName, bet.leagueName, betState);
    }

    private constructBetBuilderInfo(bet: MyBetsBet, betslip: MyBetsBetSlip): string {
        return (
            bet.betBuilderInfo?.legs
                .map((leg) => {
                    const betMarketInfo = this.myBetsVisualizationService.getBetMarketInfoFromLeg(leg);
                    const visualizationType = this.myBetsVisualizationService.getVisualizationDetails(
                        bet.betBuilderInfo?.event,
                        bet.sportId,
                        leg.marketId,
                    ).visualizationType;
                    const betState = this.getBetState(bet, betslip.isCachedOut, visualizationType, betMarketInfo);

                    return this.buildBetInfoString(betslip.sgpType ?? betslip.betType, visualizationType, leg.optionName, bet.leagueName, betState);
                })
                .join(';') || ''
        );
    }

    private buildBetInfoString(
        betType: string,
        visualizationType: string,
        optionName: string | undefined,
        leagueName: string,
        betState: string,
    ): string {
        return `${betType}-${visualizationType}-${optionName?.toLowerCase() || ''}-${leagueName.toUpperCase()}-${betState.toLowerCase()}`;
    }

    private getBetState(bet: MyBetsBet, isCachedOut: boolean, visualizationType: string, betMarketInfo: BetMarketInfo | null): string {
        if (isCachedOut) {
            return this.sitecore.earlyPayoutV2.PaidOut;
        }

        if (bet.state === BetState.Canceled) {
            return VisualizationBetState.Cancelled;
        }

        if (bet.state === BetState.Open && visualizationType !== VisualizationType.NoVisualization) {
            return betMarketInfo && this.hasStats(bet, betMarketInfo) ? VisualizationConstants.InProgressState : VisualizationConstants.ZeroState;
        }

        return bet.state;
    }

    private hasStats(bet: MyBetsBet, betMarketInfo: BetMarketInfo): boolean {
        const { marketStatsInfo, statsType } = this.myBetsVisualizationService.marketStatsDetails(bet.event, bet.marketId);
        if (!marketStatsInfo) {
            return false;
        }

        const { player1Stat, player2Stat } = this.myBetsVisualizationService.getCurrentStatValue(
            bet.event,
            statsType,
            marketStatsInfo,
            betMarketInfo,
        );

        return !!(player1Stat || player2Stat);
    }
}
